import {
  addressActions,
  AddressType,
  applyUnsanitizedAddressLines,
  Coordinates,
  selectBillingAddressVpp,
  selectUnsanitizedBillingAddressLines,
} from '@buy-viasat/redux/src/address';
import { FormFieldState } from '@buy-viasat/redux/src/types';
import { selectEmail, selectPhone } from '@buy-viasat/redux/src/personal-information';
import { selectAccountHolderEmail } from '@buy-viasat/redux/src/business-information';
import {
  appActions,
  Country,
  CustomerType,
  Modals,
  selectCountry,
  selectCustomerType,
  selectPartyId,
  selectRelnId,
} from '@buy-viasat/redux/src/app';
import { navActions, Routes } from '@buy-viasat/redux/src/navigator';
import {
  ModifyVPPTransactionPayload,
  paymentInformationActions,
  PaymentMethods,
  QueryPaymentOnFile,
  selectIsPaymentInformationLoadingStartTime,
  selectNonRegulatedSpbBillingAccountId,
  selectRegulatedSpbBillingAccountId,
  selectSmbBillingAccountId,
  selectSpbBillingAccountId,
  selectTaxCode,
  selectTransactionId,
} from '@buy-viasat/redux/src/payment-information';
import { sanitizeAddressLines, selectIsRegulated } from '@buy-viasat/redux/src/plan';
import {
  selectCoordinates,
  selectServiceAddressValues,
  selectUnsanitizedAddressLines,
} from '@buy-viasat/redux/src/serviceability';
import { AddressInput, ErrorTypes, TaxCode } from '@buy-viasat/types/build/bv';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  call,
  CallEffect,
  delay,
  Effect,
  fork,
  ForkEffect,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeLatest,
} from 'redux-saga/effects';
import { trackLoadTime } from 'shared/containers/Analytics';
import { benchmark } from 'shared/containers/App/saga';
import updateRelationshipInstanceAsync from 'shared/providers/requests/customer/updateRelationshipInstance';

import getPaymentOnFile, { GetPaymentOnFileResponse } from 'shared/providers/requests/payments/getPaymentOnFile';
import addBillingAccountAsync from '../../providers/requests/customer/addBillingAccount';
import updateBillingAccountAsync, {
  UpdateBillingAccountResponse,
} from '../../providers/requests/customer/updateBillingAccount';
import createVPPTransactionAsync from '../../providers/requests/payments/createVPPTransaction';
import getTaxCodesAsync from '../../providers/requests/payments/getTaxCodes';
import clientLogger from '../../utils/clientLogger';
import { personalInformationActions } from '../PersonalInformation/slice';
import { onPaymentInformationButtonPress } from 'shared/Navigator/saga';
import { getBillingAccountIdToDeactivate, normalizeToArray } from 'shared/utils';
import { selectFlowId } from '@buy-viasat/redux/src/auth';
import { isDefinedString } from '@buy-viasat/utils';
import { postVPPMessage } from '@buy-viasat/payment-information/src/services/vppMessage';
import modifyVPPTransactionAsync from 'shared/providers/requests/payments/modifyVPPTransaction';
import { selectOnetimeTotalWithPromo } from '@buy-viasat/redux/src/cart';
import getVPPIframeCredsAsync from 'shared/providers/requests/payments/getVPPIframeCreds';
import { BillingAccountIdTypes, CreateVPPTransactionResponse, TWO_SECONDS_MILLIS } from '../../types';

import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';

export function* setLoadingFalseAfterDelay(): Generator {
  yield delay(TWO_SECONDS_MILLIS);
  yield put(paymentInformationActions.setIsPaymentInformationLoading(false));
}

export function* getVPPPaymentOnFileSaga(action: PayloadAction<QueryPaymentOnFile>) {
  try {
    const response: GetPaymentOnFileResponse = yield call(getPaymentOnFile, {
      systemName: action.payload.systemName,
      customerRef: action.payload.customerRef,
    });
    if (response?.data?.getPaymentOnFile) {
      yield put(paymentInformationActions.setPaymentOnFileSuccess(response.data.getPaymentOnFile));
    }
    return response;
  } catch (err: any) {
    clientLogger('getVPPPaymentOnFileSaga Error: ', err);
    if (err.message === ErrorTypes.PaymentAuthError) yield put(appActions.setModalVisible(Modals.AUTH_ERROR));
    throw err;
  }
}

function* advanceToNextStep() {
  yield put(appActions.setIsPaymentInformationComplete(true));
  if (!USE_NEW_API) {
    yield call(onPaymentInformationButtonPress);
  }
}

function* modifyVPPTransactionSaga(action: PayloadAction<ModifyVPPTransactionPayload>) {
  try {
    const modifyVPPTransactionResult: string = yield call(modifyVPPTransactionAsync, {
      transactionId: action.payload.transactionId,
      oneTimeTotal: action.payload.transactionAmount,
      customerType: action.payload.customerType,
      userAgent: action.payload.userAgent,
    });

    if (modifyVPPTransactionResult) {
      const creds: { dataSecret: string } = yield call(fetchVPPIframeCreds);

      if (!creds.dataSecret) {
        throw new Error('Missing VPP data secret');
      }
      postVPPMessage(
        {
          street: action.payload.billingAddressPayload.street,
          houseNumberOrName: action.payload.billingAddressPayload.houseNumberOrName,
          city: action.payload.billingAddressPayload.city,
          stateOrProvince: action.payload.billingAddressPayload.stateOrProvince,
          postalCode: action.payload.billingAddressPayload.postalCode,
        },
        creds.dataSecret,
      );
    }
  } catch (e) {
    yield put(paymentInformationActions.setIsPaymentInformationLoading(false));
    clientLogger('modifyVPPTransactionSaga Error: ', e);

    const spbBillingAccountId: string = yield select(selectSpbBillingAccountId);
    if (spbBillingAccountId) {
      clientLogger('Creating new VPP transaction after modification failure', e);
      yield put(paymentInformationActions.createVPPTransaction(spbBillingAccountId));
    }

    yield put(appActions.setErrorRetryAction({ type: appActions.onModalCancel.type, payload: null }));
    yield put(appActions.setModalVisible(Modals.ERROR));
    yield put(appActions.setIsCheckoutButtonDisabled(false));
  }
}

export function* createVPPTransactionSaga(
  action: PayloadAction<string>,
): Generator<Effect, void, string & CustomerType & AddressInput & number & string[]> {
  const relnId: string = yield select(selectRelnId);
  const customerType: CustomerType = yield select(selectCustomerType);
  const billingAddressValues = yield select(selectBillingAddressVpp);
  const unsanitizedBillingAddressLines: string[] | null = yield select(selectUnsanitizedBillingAddressLines);
  const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
  const billingAddress = applyUnsanitizedAddressLines(
    billingAddressValues,
    unsanitizedBillingAddressLines ?? unsanitizedAddressLines,
  );
  const transactionId: string = yield select(selectTransactionId);
  const oneTimeTotalWithPromo: number = yield select(selectOnetimeTotalWithPromo);
  const phone: FormFieldState<string> = yield select(selectPhone);

  let spbBillingAccountId = action.payload;
  if (!spbBillingAccountId || spbBillingAccountId === '') {
    spbBillingAccountId = yield select(selectSpbBillingAccountId);
  }

  if (!spbBillingAccountId) {
    clientLogger('createVPPTransactionSaga: Missing billing account ID', new Error('No billing account'));
    yield put(paymentInformationActions.setIsPaymentInformationLoading(false));
    yield put(appActions.setModalVisible(Modals.ERROR));
    return;
  }

  if (isDefinedString(transactionId) && transactionId === '') {
    yield put(paymentInformationActions.resetTransactionId());
  }

  let email = '';
  let emailField: FormFieldState<string>;

  switch (customerType) {
    case CustomerType.B:
      email = yield select(selectAccountHolderEmail);
      break;
    case CustomerType.R:
      emailField = yield select(selectEmail);
      email = emailField?.value;
      break;
  }

  const oneTimeTotal = oneTimeTotalWithPromo > 0 ? oneTimeTotalWithPromo : 0;
  try {
    const createVPPResult = (yield call(() =>
      createVPPTransactionAsync({
        relnId,
        email: email,
        billingAddress,
        oneTimeTotal,
        spbBillingAccountId: spbBillingAccountId,
        userAgent: navigator.userAgent,
        customerType,
        phone: phone.value,
      }),
    )) as CreateVPPTransactionResponse;
    const resultTransactionId: string = createVPPResult.data.createVPPTransaction.transactionId;
    yield put(paymentInformationActions.setTransactionId(resultTransactionId));
    yield fork(setLoadingFalseAfterDelay);
  } catch (err: any) {
    yield put(paymentInformationActions.setIsPaymentInformationLoading(false));
    clientLogger('createVPPTransactionSaga Error: ', err);
    yield put(
      appActions.setErrorRetryAction({ type: paymentInformationActions.createVPPTransaction.type, payload: null }),
    );
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

function getBillingAccountsToRemove(accountIdTypes: BillingAccountIdTypes): string[] {
  const values = Object.values(accountIdTypes);
  const result = values.filter((value) => !!value);
  return result;
}

function* clearBillingDataSaga() {
  yield put(paymentInformationActions.setSpbBillingAccountId(''));
  yield put(paymentInformationActions.setSmbBillingAccountId(''));
  yield put(paymentInformationActions.setTransactionId(''));
  yield put(paymentInformationActions.setRegulatedSpbBillingAccountId(''));
  yield put(paymentInformationActions.setNonRegulatedSpbBillingAccountId(''));
}

function* processBillingAccountBasedOnTaxCode(billingAccountId: string, isTaxCodeDifferent: boolean) {
  yield put(paymentInformationActions.setSpbBillingAccountId(billingAccountId));

  if (isTaxCodeDifferent) {
    yield put(paymentInformationActions.updateBillingAccount());
  }

  yield call(updateRelationshipInstanceSaga);
}

function* handleMissingBillingAccount(accountIds: BillingAccountIdTypes) {
  const result = getBillingAccountsToRemove(accountIds);

  const existingBillingAccountId: string = yield select(selectSpbBillingAccountId);

  if (isDefinedString(existingBillingAccountId)) {
    clientLogger(`[PROTECTION] Billing account already exists, skipping addBillingAccount action`, {
      existingId: existingBillingAccountId,
    });
    yield call(updateRelationshipInstanceSaga);
    return;
  }

  yield put(paymentInformationActions.addBillingAccount(result));
}

export function* getTaxCodesSaga() {
  try {
    if (USE_NEW_API) {
      return;
    }

    const taxCode: TaxCode = yield select(selectTaxCode);
    const serviceAddress: AddressType = yield select(selectServiceAddressValues);
    const coordinates: Coordinates = yield select(selectCoordinates);
    const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
    const isRegulated: boolean = yield select(selectIsRegulated);
    const regulatedSPBBillingAccountId: string = yield select(selectRegulatedSpbBillingAccountId);
    const nonRegulatedSPBBillingAccountId: string = yield select(selectNonRegulatedSpbBillingAccountId);
    const smbBillingAccountId: string = yield select(selectSmbBillingAccountId);
    const customerType: string = yield select(selectCustomerType);

    const { data } = yield call(
      getTaxCodesAsync,
      sanitizeAddressLines({ address: serviceAddress, coordinates }, unsanitizedAddressLines),
    );
    const taxCodes: TaxCode[] = data.getTaxCodes;
    const isTaxCodeDifferent =
      taxCode.taxCodeType !== taxCodes?.[0].taxCodeType || taxCode.taxCodeValue !== taxCodes?.[0].taxCodeValue;

    if (isTaxCodeDifferent) {
      yield put(paymentInformationActions.setTaxCode(taxCodes[0]));
    }

    if (isRegulated) {
      if (isDefinedString(regulatedSPBBillingAccountId)) {
        yield* processBillingAccountBasedOnTaxCode(regulatedSPBBillingAccountId, isTaxCodeDifferent);
      } else {
        yield* handleMissingBillingAccount({ smbBillingAccountId, nonRegulatedSPBBillingAccountId });
      }
    } else if (customerType === CustomerType.B) {
      if (isDefinedString(smbBillingAccountId)) {
        yield* processBillingAccountBasedOnTaxCode(smbBillingAccountId, isTaxCodeDifferent);
      } else {
        yield* handleMissingBillingAccount({ regulatedSPBBillingAccountId, nonRegulatedSPBBillingAccountId });
      }
    } else {
      if (isDefinedString(nonRegulatedSPBBillingAccountId)) {
        yield* processBillingAccountBasedOnTaxCode(nonRegulatedSPBBillingAccountId, isTaxCodeDifferent);
      } else {
        yield* handleMissingBillingAccount({ regulatedSPBBillingAccountId, smbBillingAccountId });
      }
    }
  } catch (err: any) {
    yield put(appActions.setErrorRetryAction({ type: paymentInformationActions.getTaxCodes.type, payload: null }));
    yield put(personalInformationActions.onSagaError({ callingSaga: 'getTaxCodesSaga', err, payload: null }));
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

function* onEditBillingAddressSaga() {
  const country: Country = yield select(selectCountry);

  yield put(appActions.setIsCartVisible(false));
  yield put(paymentInformationActions.setIsPaymentInformationLoading(false));
  yield put(addressActions.setBillingNotEqualToServiceAddress());
  yield put(addressActions.resetBillingAddress(country));
  yield put(appActions.setIsModalVisible(false));
  yield put(navActions.routeUserTo(Routes.PERSONAL_INFORMATION));
}

export function* updateRelationshipInstanceSaga(): Generator<Effect, void, string> {
  const relnId: string = yield select(selectRelnId);
  const spbBillingAccountId: string = yield select(selectSpbBillingAccountId);
  const regulatedSPBBillingAccountId: string = yield select(selectRegulatedSpbBillingAccountId);
  const nonRegulatedSPBBillingAccountId: string = yield select(selectNonRegulatedSpbBillingAccountId);
  const smbBillingAccountId: string = yield select(selectSmbBillingAccountId);

  const deactivateBillingAccountArg = {
    nonRegulatedSPBBillingAccountId,
    smbBillingAccountId,
    regulatedSPBBillingAccountId,
    spbBillingAccountId,
  };
  const deactivateAccountId = getBillingAccountIdToDeactivate(deactivateBillingAccountArg);
  try {
    yield call(updateRelationshipInstanceAsync, {
      relnId,
      accountNumber: spbBillingAccountId,
      accountNumbersToRemove: deactivateAccountId,
    });
  } catch (err: any) {
    yield put(
      personalInformationActions.onSagaError({ callingSaga: 'updateRelationshipInstanceSaga', err, payload: null }),
    );
    yield put(appActions.setErrorRetryAction({ type: appActions.onModalCancel.type, payload: null }));
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

function* addSPBBillingAccountSaga(
  action: PayloadAction<string>,
): Generator<
  SelectEffect | CallEffect | PutEffect | ForkEffect,
  void,
  string & FormFieldState<string> & AddressType & number
> {
  const taxCode: TaxCode = yield select(selectTaxCode);
  const relnId: string = yield select(selectRelnId);
  const partyId: string = yield select(selectPartyId);
  const isRegulated: boolean = yield select(selectIsRegulated);
  const customerType: CustomerType = yield select(selectCustomerType);
  const country: Country = yield select(selectCountry);

  const { taxCodeType, taxCodeValue } = taxCode;

  if (!partyId) {
    yield put(appActions.setModalVisible(Modals.ERROR));
    return;
  }

  const billingAccountInput = {
    relnId,
    partyId,
    recurringPaymentMethodType: PaymentMethods.VISA,
    taxCode: {
      taxCodeType,
      taxCodeValue,
    },
    isRegulated,
    accountNumbersToRemove: normalizeToArray(action.payload),
    customerType,
    country,
  };

  try {
    const {
      data: {
        addBillingAccount: { accountNumber },
      },
    } = yield call(addBillingAccountAsync, billingAccountInput);

    yield put(paymentInformationActions.setSpbBillingAccountId(accountNumber));

    if (isRegulated) {
      yield put(paymentInformationActions.setRegulatedSpbBillingAccountId(accountNumber));
    } else if (customerType === CustomerType.B) {
      yield put(paymentInformationActions.setSmbBillingAccountId(accountNumber));
    } else {
      yield put(paymentInformationActions.setNonRegulatedSpbBillingAccountId(accountNumber));
    }
  } catch (err: any) {
    yield put(appActions.setErrorRetryAction({ type: appActions.onModalCancel.type, payload: null }));
    yield put(personalInformationActions.onSagaError({ callingSaga: 'addSPBBillingAccountSaga', err, payload: null }));
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

function* updateBillingAccountSaga(): Generator<
  SelectEffect | CallEffect | PutEffect | ForkEffect,
  void,
  string & FormFieldState<string> & AddressType & number
> {
  const customerType: CustomerType = yield select(selectCustomerType);
  const billingAccountId: string = yield select(selectSpbBillingAccountId);
  const taxCode: TaxCode = yield select(selectTaxCode);

  const { taxCodeType, taxCodeValue } = taxCode;
  try {
    const { data }: UpdateBillingAccountResponse = yield call((args) => updateBillingAccountAsync(args), {
      accountNumber: billingAccountId,
      taxCode: {
        taxCodeType,
        taxCodeValue,
      },
      customerType,
    });

    const accountNumber = data?.updateBillingAccount?.accountNumber;
    const hasAccountNumberChanged = accountNumber && accountNumber !== billingAccountId;

    if (hasAccountNumberChanged) {
      yield put(paymentInformationActions.createVPPTransaction(accountNumber));
    }
  } catch (err: any) {
    yield put(
      appActions.setErrorRetryAction({ type: paymentInformationActions.updateBillingAccount.type, payload: null }),
    );
    yield put(personalInformationActions.onSagaError({ callingSaga: 'updateBillingAccountSaga', err, payload: null }));
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

export function* vppPostMessageSuccessSaga(): Generator {
  const startTime = yield select(selectIsPaymentInformationLoadingStartTime);
  yield call(advanceToNextStep);
  const flowId = yield select(selectFlowId);
  trackLoadTime(
    startTime as number,
    performance.now(),
    paymentInformationActions.setVppPostMessageSuccess.type,
    flowId as string,
  );
  yield put(paymentInformationActions.setIsPaymentInformationLoadingStartTime(0));
}

export function* vppPostMessageFailureSaga(): Generator {
  const startTime = yield select(selectIsPaymentInformationLoadingStartTime);
  const flowId = yield select(selectFlowId);
  trackLoadTime(
    startTime as number,
    performance.now(),
    paymentInformationActions.setVPPPostMessageFailure.type,
    flowId as string,
  );
  yield put(paymentInformationActions.setIsPaymentInformationLoadingStartTime(0));
}

function* fetchVPPIframeCreds() {
  try {
    const { data } = yield call(getVPPIframeCredsAsync);

    return data.getVPPIframeCreds;
  } catch (err: any) {
    yield call(clientLogger, 'fetchVPPIframeDataSecret: ', err);

    return null;
  }
}

export function* getVPPIframeCredsSaga() {
  // @ts-expect-error — fetchVPPIframeCreds returns unknown, typed downstream
  const creds = yield call(fetchVPPIframeCreds);

  if (creds) {
    yield put(paymentInformationActions.setVPPIframeCreds(creds));
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export function* paymentInformationSaga(): Generator {
  yield takeLatest(paymentInformationActions.modifyVPPTransaction.type, benchmark(modifyVPPTransactionSaga));
  yield takeLatest(paymentInformationActions.createVPPTransaction.type, benchmark(createVPPTransactionSaga));
  yield takeLatest(paymentInformationActions.setPaymentOnFileRequested.type, benchmark(getVPPPaymentOnFileSaga));
  yield takeLatest(paymentInformationActions.getTaxCodes.type, benchmark(getTaxCodesSaga));
  yield takeLatest(paymentInformationActions.clearBillingData.type, clearBillingDataSaga);
  yield takeLatest(paymentInformationActions.addBillingAccount.type, benchmark(addSPBBillingAccountSaga));
  yield takeLatest(paymentInformationActions.updateBillingAccount.type, benchmark(updateBillingAccountSaga));
  yield takeLatest(paymentInformationActions.setVppPostMessageSuccess.type, vppPostMessageSuccessSaga);
  yield takeLatest(paymentInformationActions.setVPPPostMessageFailure.type, vppPostMessageFailureSaga);
  yield takeLatest(paymentInformationActions.onEditBillingAddress.type, benchmark(onEditBillingAddressSaga));
  yield takeLatest(paymentInformationActions.getVPPIframeCreds.type, benchmark(getVPPIframeCredsSaga));
}
